import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography,
    Card,
    Space,
    Button,
    message,
    Skeleton,
    Input, Row, Col, Tag, Result
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/authcontext';
import { PageVersionConflict } from '../types/pageversiontypes';
import { getPageVersionApprovalById, reviewPageVersionApproval } from '../services/pageversionservice';

const { Text } = Typography;
const { TextArea } = Input;

interface ReviewPageVersionApprovalDto {
    pageVersionGuid: string;
    isApproved: boolean;
    reviewerGuid: string;
    comment: string;
    pageVersionName?: string;
    requestedBy?: string;
    startDate?: string;
    endDate?: string;
    storeNumbers?: string[];
    override?: boolean;
    conflicts?: PageVersionConflict[];
}

const ReviewApproval: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [approval, setApproval] = useState<ReviewPageVersionApprovalDto | null>(null);
    const [comment, setComment] = useState<string>('');
    const { userId, isReadOnly } = useAuth();
    const reviewerGuid = userId || '';
    const [error, setError] = useState<string | null>(null);
    const [submittingAction, setSubmittingAction] = useState<'approve' | 'reject' | null>(null);
    const [commentError, setCommentError] = useState<string | null>(null);

    useEffect(() => {
        const fetchApproval = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await getPageVersionApprovalById(id!);
                setApproval(data);
            } catch (error: unknown) {
                setApproval(null);
                console.error("Error fetching approval:", error);
                setError("Failed to load approval. Please try again.");                                   
            } finally {
                setLoading(false);
            }
        };
        fetchApproval();
    }, [id]);

    const handleDecision = async (isApproved: boolean) => {
        if (!approval) return;

        if (!comment.trim()) {
            setCommentError('Review comment is required.');
            return;
        }

        setCommentError(null);
        const action = isApproved ? 'approve' : 'reject';
        setSubmittingAction(action);

        const payload: ReviewPageVersionApprovalDto = {
            pageVersionGuid: approval.pageVersionGuid,
            isApproved,
            reviewerGuid,
            comment,
            pageVersionName: approval.pageVersionName,
            requestedBy: approval.requestedBy
        };

        try {
            await reviewPageVersionApproval(payload);
            message.success(`Request ${isApproved ? 'approved' : 'rejected'} successfully.`);
            navigate('/approvals');
        }
        catch (error: any) {
            if (error?.response?.data) {
                const { message: errorMessage, errorCode } = error.response.data;

                if (errorCode === 'CONFLICT_VALIDATION_FAILED') {
                    message.error(errorMessage);
                    navigate('/approvals');
                } else {
                    message.error(errorMessage || 'Failed to submit decision.');
                }
            } else {
                message.error('Something went wrong. Please try again.');
            }
        }

        finally {
            setSubmittingAction(null);
        }
    };

    if (loading) {
        return (
            <Card>
                <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
        );
    }

    if (error === 'Approval not found. It may have already been reviewed.') {
        return (
            <Result
                status="404"
                title="404"
                subTitle={error}
                extra={<Button type="primary" onClick={() => navigate('/approvals')}>Go to Approvals</Button>}
            />
        );
    }

    if (error) {
        return (
            <Card>
                <Text type="danger">{error}</Text>
            </Card>
        );
    }

    if (!approval) {
        return (
            <Card>
                <Text type="warning">No approval data available.</Text>
            </Card>
        );
    }


    return (
        <Card
            title="Review PageVersion Approval Request"
            styles={{
                header: {
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    borderRadius: '8px 8px 0 0',
                    minHeight: '36px',
                },
                body: {
                    padding: '24px',
                },
            }}
            style={{
                width: '68vw',
                margin: '2rem auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: '8px',
            }}
        >
            <Row gutter={32} style={{ marginBottom: '2rem' }}>
                <Col xs={24} md={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary">PageVersion Name</Text>
                        <Text strong>{approval.pageVersionName}</Text>
                    </Space>
                </Col>

                <Col xs={24} md={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary">Requested By</Text>
                        <Text>{approval.requestedBy}</Text>
                    </Space>
                </Col>

                <Col xs={24} md={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary">Start Date</Text>
                        <Text>{approval.startDate ? new Date(approval.startDate).toLocaleString() : '—'}</Text>
                    </Space>
                </Col>

                <Col xs={24} md={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary">End Date</Text>
                        <Text>{approval.endDate ? new Date(approval.endDate).toLocaleString() : '—'}</Text>
                    </Space>
                </Col>
            </Row>

            <Row style={{ marginBottom: '2rem' }}>
                <Col span={24}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text type="secondary">Store Numbers</Text>
                        <Space wrap>
                            {approval.storeNumbers?.map((num) => (
                                <Tag key={num}>{num}</Tag>
                            ))}
                        </Space>

                    </Space>
                </Col>
            </Row>

            <Row style={{ marginBottom: '2rem' }}>
                <Col span={24}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text type="secondary">Override Request</Text>

                        <Tag color={approval.override ? 'red' : 'green'}>
                            {approval.override ? 'Yes (Override Enabled)' : 'No'}
                        </Tag>

                        {approval.override && (
                            <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                                This will publish pageversion with new schedule along with existing scheduled pageversions for selected stores, if conflicting pageversions are not required, mark them as Inactive.
                            </Tag>
                        )}
                    </Space>
                </Col>
            </Row>


            {approval.conflicts && approval.conflicts.length > 0 && (
                <Row style={{ marginBottom: '2rem' }}>
                    <Col span={24}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text type="secondary">Conflicts at Submission</Text>

                            {approval.conflicts.map((c) => (
                                <Card size="small" key={c.storeGuid} style={{ width: '100%' }}>
                                    <Text strong>
                                        {c.storeNumber} - {c.buildingName}
                                    </Text>

                                    <ul style={{ marginTop: 8 }}>
                                        {c.overlappingPageVersions.map((pv) => (
                                            <li key={pv.pageVersionName}>
                                                {pv.pageVersionName} (
                                                {new Date(pv.startDate).toLocaleDateString()} →{' '}
                                                {new Date(pv.endDate).toLocaleDateString()}
                                                ) [{pv.conflictType}]
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            ))}
                        </Space>
                    </Col>
                </Row>
            )}

            <div style={{ marginTop: '2rem' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">
                        Review Comment <span style={{ color: 'red' }}>*</span>
                    </Text>
                    <TextArea
                        rows={4}
                        placeholder="Add your review comment here..."
                        value={comment}
                        onChange={(e) => {
                            setComment(e.target.value);
                            if (commentError) setCommentError(null);
                        }}
                        allowClear
                        style={{ width: '100%' }}
                    />
                    {commentError && <Text type="danger">{commentError}</Text>}
                </Space>
            </div>

            {!isReadOnly && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                    <Space>
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleDecision(true)}
                            loading={submittingAction === 'approve'}
                            disabled={submittingAction !== null}
                        >
                            Approve
                        </Button>

                        <Button
                            danger
                            icon={<CloseCircleOutlined />}
                            onClick={() => handleDecision(false)}
                            loading={submittingAction === 'reject'}
                            disabled={submittingAction !== null}
                        >
                            Reject
                        </Button>
                    </Space>
                </div>
            )}
        </Card>
    );
};

export default ReviewApproval;